"use client"

import Image from "next/image"
import { m } from "framer-motion"
import { cn } from "@/lib/utils"

export type HeroBiomarkerOverlay = {
  /** Biomarker label, e.g. "Visceral fat" */
  label: string
  /** Result value displayed prominently, e.g. "2.2L" */
  value: string
  /** Status pill text, e.g. "Normal range" */
  status: string
}

export type HeroFloatingImage = {
  src: string
  alt?: string
  /** Optional caption above the image. */
  caption?: string
  /** Width of the image card in px (height auto from natural ratio). */
  width?: number
}

export type HeroBiomarkerImageProps = {
  src: string
  alt?: string
  /** Bottom-left overlay card (visible from `lg:` up). */
  primary?: HeroBiomarkerOverlay
  /** Top-right overlay card (visible from `lg:` up). */
  secondary?: HeroBiomarkerOverlay
  /** Optional bottom-right floating image (visible from `lg:` up). */
  floatingImage?: HeroFloatingImage
  className?: string
  priority?: boolean
}

function OverlayCard({
  position,
  label,
  value,
  status,
  delay,
  fromY,
}: HeroBiomarkerOverlay & {
  position: "bottom-left" | "top-right"
  delay: number
  fromY: number
}) {
  return (
    <m.div
      initial={{ opacity: 0, y: fromY }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1], delay }}
      className={cn(
        "absolute z-10 hidden max-w-[220px] rounded-[12px] bg-white/70 p-[18px] backdrop-blur-[10px] lg:block",
        position === "bottom-left" ? "bottom-4 left-4" : "top-4 right-4"
      )}
    >
      <p className="type-body-lg font-medium text-black">{label}</p>
      <span className="type-title-lg mt-0.5 block font-medium text-black">
        {value}
      </span>
      <span className="type-label-md mt-1 inline-block rounded-[37px] bg-[rgba(184,199,101,0.52)] px-[9px] py-[5px] font-medium text-neutral-900">
        {" "}
        {/* ds-color-ignore: translucent overlay (alpha) */}
        {status}
      </span>
    </m.div>
  )
}

export function HeroBiomarkerImage({
  src,
  alt = "",
  primary,
  secondary,
  floatingImage,
  className,
  priority = true,
}: HeroBiomarkerImageProps) {
  return (
    <div
      className={cn(
        "relative w-full overflow-hidden rounded-[16px]",
        // Default aspect-square; override by passing e.g. "aspect-[567/420]" in className.
        !className?.includes("aspect-") && "aspect-square",
        className
      )}
    >
      <Image
        src={src}
        alt={alt}
        fill
        priority={priority}
        fetchPriority={priority ? "high" : "auto"}
        sizes="(min-width: 768px) 567px, 100vw"
        className="object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-black/10" />
      {primary && (
        <OverlayCard
          position="bottom-left"
          delay={0.5}
          fromY={12}
          {...primary}
        />
      )}
      {secondary && (
        <OverlayCard
          position="top-right"
          delay={0.7}
          fromY={-12}
          {...secondary}
        />
      )}
      {floatingImage && (
        <div
          className="absolute right-4 bottom-4 z-10 hidden rounded-[12px] bg-white/85 p-2 shadow-[0_12px_32px_rgba(0,0,0,0.18)] backdrop-blur-[10px] lg:block"
          style={{ width: floatingImage.width ?? 132 }}
        >
          <Image
            src={floatingImage.src}
            alt={floatingImage.alt ?? ""}
            width={floatingImage.width ?? 132}
            height={Math.round((floatingImage.width ?? 132) * 1.07)}
            unoptimized
            className="block h-auto w-full rounded-[8px] object-cover"
          />
        </div>
      )}
    </div>
  )
}
